import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import LoadingSpinner from './components/LoadingSpinner';
import Analytics from './components/Analytics';

// Eagerly loaded components
import SurveyList from './components/SurveyList';
import SurveyForm from './components/SurveyForm';
import ThankYou from './components/ThankYou';
import WebhookTest from './components/WebhookTest';
import ContactButton from './components/ContactButton';
import ContactModal from './components/ContactModal';
import BookingForm from './components/BookingForm';
import CalWidget from './components/CalWidget';
import ProtectedRoute from './components/ProtectedRoute';
import AdminVideos from './components/AdminVideos';
import AdminCRMContacts from './components/AdminCRMContacts';
import AdminCRMContactForm from './components/AdminCRMContactForm';
import AdminCRMNotes from './components/AdminCRMNotes';
import AdminCTAs from './components/AdminCTAs';
import AdminCTAEditor from './components/AdminCTAEditor';
import AdminPostEditor from './components/AdminPostEditor';
import BlogPostWithDownload from './components/BlogPostWithDownload';
import UpdateBlogPostContent from './components/UpdateBlogPostContent';
import AdminUploads from './components/AdminUploads';

// Lazily loaded components
const LandingPage = lazy(() => import('./components/LandingPage'));
const CourseCatalog = lazy(() => import('./components/CourseCatalog'));
const CourseDetail = lazy(() => import('./components/CourseDetail'));
const CourseOutline = lazy(() => import('./components/CourseOutline'));
const FractionalPMPage = lazy(() => import('./components/FractionalPMPage'));
const AIWorkshopPage = lazy(() => import('./components/AIWorkshopPage'));
const BlogLayout = lazy(() => import('./components/BlogLayout'));
const BlogList = lazy(() => import('./components/BlogList'));
const BlogPost = lazy(() => import('./components/BlogPost'));
const CategoryList = lazy(() => import('./components/CategoryList'));
const AuthorList = lazy(() => import('./components/AuthorList'));
const CategoryIndex = lazy(() => import('./components/CategoryIndex'));
const AuthorIndex = lazy(() => import('./components/AuthorIndex'));
const AdminLayout = lazy(() => import('./components/AdminLayout'));
const AdminLogin = lazy(() => import('./components/AdminLogin'));
const AdminPosts = lazy(() => import('./components/AdminPosts'));
const AdminAuthors = lazy(() => import('./components/AdminAuthors'));
const AdminCategories = lazy(() => import('./components/AdminCategories'));
const AdminTags = lazy(() => import('./components/AdminTags'));
const AboutPage = lazy(() => import('./components/AboutPage'));
const AdminCourses = lazy(() => import('./components/AdminCourses'));
const AdminCourseEditor = lazy(() => import('./components/AdminCourseEditor'));
const AdminCourseCategories = lazy(() => import('./components/AdminCourseCategories'));
const AdminCourseTags = lazy(() => import('./components/AdminCourseTags'));
const AdminAssets = lazy(() => import('./components/AdminAssets'));
const TimelinePage = lazy(() => import('./components/TimelinePage'));

function App() {
  const [isContactModalOpen, setIsContactModalOpen] = React.useState(false);
  const [isBookingOpen, setIsBookingOpen] = React.useState(false);
  const [isCalOpen, setIsCalOpen] = React.useState(false);

  // Preload all lazy components after initial render
  useEffect(() => {
    const preloadComponents = async () => {
      try {
        await Promise.all([
          import('./components/LandingPage'),
          import('./components/CourseCatalog'),
          import('./components/CourseDetail'),
          import('./components/CourseOutline'),
          import('./components/FractionalPMPage'),
          import('./components/AIWorkshopPage'),
          import('./components/BlogLayout'),
          import('./components/BlogList'),
          import('./components/BlogPost'),
          import('./components/CategoryList'),
          import('./components/AuthorList'),
          import('./components/CategoryIndex'),
          import('./components/AuthorIndex'),
          import('./components/AdminLayout'),
          import('./components/AdminLogin'),
          import('./components/AdminPosts'),
          import('./components/AdminAuthors'),
          import('./components/AdminCategories'),
          import('./components/AdminTags'),
          import('./components/AboutPage'),
          import('./components/AdminCourses'),
          import('./components/AdminCourseEditor'),
          import('./components/AdminCourseCategories'),
          import('./components/AdminCourseTags'),
          import('./components/AdminAssets'),
          import('./components/TimelinePage')
        ]);
        console.log('All components preloaded successfully');
      } catch (error) {
        console.error('Error preloading components:', error);
      }
    };

    preloadComponents();
  }, []);

  return (
    <Router>
      <Analytics measurementId="G-6QQMV5RY9V" />
      
      <div className="min-h-screen bg-gray-50">
        <Suspense fallback={
          <div className="min-h-screen flex items-center justify-center">
            <LoadingSpinner size="lg" />
          </div>
        }>
          <Routes>
            <Route path="/" element={<LandingPage onContactClick={() => setIsContactModalOpen(true)} onCalClick={() => setIsCalOpen(true)} />} />
            <Route path="/courses" element={<CourseCatalog />} />
            <Route path="/course/:slug" element={<CourseDetail />} />
            <Route path="/course-outline" element={<CourseOutline />} />
            <Route path="/ai-workshop" element={<AIWorkshopPage />} />
            <Route path="/fractional-pm" element={<FractionalPMPage onCalendarClick={() => setIsCalOpen(true)} />} />
            <Route path="/survey" element={<SurveyList />} />
            <Route path="/survey/:id" element={<SurveyForm />} />
            <Route path="/thank-you" element={<ThankYou onCalClick={() => setIsCalOpen(true)} />} />
            <Route path="/webhook-test" element={<WebhookTest />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/timeline" element={<TimelinePage />} />
            <Route path="/update-blog-post" element={<UpdateBlogPostContent />} />
            
            <Route path="/blog" element={<BlogLayout />}>
              <Route index element={<BlogList />} />
              <Route path="categories" element={<CategoryIndex />} />
              <Route path="authors" element={<AuthorIndex />} />
              <Route path="category/:category" element={<CategoryList />} />
              <Route path="author/:author" element={<AuthorList />} />
              <Route path=":slug" element={<BlogPost />} />
            </Route>

            {/* Special route for the non-profit AI blog post */}
            <Route path="/blog/why-non-profits-cant-afford-to-ignore-ai-any-longer" element={
              <BlogLayout>
                <BlogPostWithDownload slug="why-non-profits-cant-afford-ignore-ai-any-longer" />
              </BlogLayout>
            } />

            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }>
              <Route index element={null} />
              <Route path="posts" element={<AdminPosts />} />
              <Route path="posts/new" element={<AdminPostEditor />} />
              <Route path="posts/:id" element={<AdminPostEditor />} />
              <Route path="courses" element={<AdminCourses />} />
              <Route path="courses/new" element={<AdminCourseEditor />} />
              <Route path="courses/:id" element={<AdminCourseEditor />} />
              <Route path="course-categories" element={<AdminCourseCategories />} />
              <Route path="course-tags" element={<AdminCourseTags />} />
              <Route path="authors" element={<AdminAuthors />} />
              <Route path="categories" element={<AdminCategories />} />
              <Route path="tags" element={<AdminTags />} />
              <Route path="assets" element={<AdminAssets />} />
              <Route path="videos" element={<AdminVideos />} />
              <Route path="uploads" element={<AdminUploads />} />
              <Route path="ctas" element={<AdminCTAs />} />
              <Route path="ctas/new" element={<AdminCTAEditor />} />
              <Route path="ctas/:id" element={<AdminCTAEditor />} />
              {/* CRM Routes */}
              <Route path="crm/contacts" element={<AdminCRMContacts />} />
              <Route path="crm/contacts/new" element={<AdminCRMContactForm />} />
              <Route path="crm/contacts/:id" element={<AdminCRMContactForm />} />
              <Route path="crm/contacts/:id/notes" element={<AdminCRMNotes />} />
            </Route>
          </Routes>
        </Suspense>

        <ContactButton 
          onClick={() => setIsContactModalOpen(true)}
          onCalendarClick={() => setIsBookingOpen(true)}
          onCalClick={() => setIsCalOpen(true)}
        />
        <ContactModal 
          isOpen={isContactModalOpen}
          onClose={() => setIsContactModalOpen(false)}
        />
        <BookingForm
          isOpen={isBookingOpen}
          onClose={() => setIsBookingOpen(false)}
        />
        <CalWidget
          isOpen={isCalOpen}
          onClose={() => setIsCalOpen(false)}
        />
      </div>
    </Router>
  );
}

export default App;